import {
  cloneElement,
  createContext,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
  useRef,
} from "react";

import { matchRoute, type PathPattern, useLocation, useRouter } from "wouter";

const RouteMatchRefContext = createContext<React.MutableRefObject<boolean> | null>(null);

type RouteLikeElement = ReactElement<{ path?: string; nest?: boolean; match?: unknown }>;

const flattenChildren = (children: ReactNode): ReactNode[] =>
  Array.isArray(children)
    ? children.flatMap((child) =>
        flattenChildren(
          child && isValidElement(child) && child.type === Fragment
            ? (child as ReactElement<{ children: ReactNode }>).props.children
            : child,
        ),
      )
    : [children];

/**
 * Resets on each render so sibling route switches can mark a match before the catch-all runs.
 *
 * Subscribes to the pathname because the switches do too. A client-side redirect (sign-in,
 * or `/` to the role home) otherwise leaves the previous match set, and the next page
 * renders nothing until a full reload.
 *
 * Must render inside the Suspense boundary that catches lazy routes. A lazy page suspends
 * after a switch has already set the flag; React then retries only that boundary. An ancestor
 * scope would not reset, and the retry would render nothing.
 */
export function AppRouteMatchScope({ children }: { children: ReactNode }) {
  // Re-render on navigation so the flag clears before the switches render.
  useLocation();
  const matchedRef = useRef(false);
  matchedRef.current = false;

  return (
    <RouteMatchRefContext.Provider value={matchedRef}>{children}</RouteMatchRefContext.Provider>
  );
}

function reportRouteMatch(ref: React.MutableRefObject<boolean> | null): void {
  if (ref) {
    ref.current = true;
  }
}

/**
 * Drop-in replacement for wouter's Switch that marks the current location as matched
 * when any child route matches. Used with {@link AppNotFoundIfUnmatched}.
 */
export function ReportingSwitch({ children, location }: { children: ReactNode; location?: string }) {
  const router = useRouter();
  const [originalLocation] = useLocation();
  const matchRef = useContext(RouteMatchRefContext);

  // Sibling switches each pick their own match. If two list the same path
  // (SFS + client both had /audit-report), both Layouts render stacked.
  if (matchRef?.current) {
    return null;
  }

  for (const element of flattenChildren(children)) {
    if (!isValidElement(element)) {
      continue;
    }

    const routeElement = element as RouteLikeElement;
    const match = matchRoute(
      router.parser,
      routeElement.props.path as PathPattern,
      location ?? originalLocation,
      routeElement.props.nest,
    );

    if (match[0]) {
      reportRouteMatch(matchRef);
      return cloneElement(routeElement, { match });
    }
  }

  return null;
}

export function AppNotFoundIfUnmatched({ children }: { children: ReactNode }) {
  const matchRef = useContext(RouteMatchRefContext);
  if (matchRef?.current) {
    return null;
  }

  return children;
}
